/*
 * @Author: wuzhimin14
 * @Date: 2025-01-XX
 * @Description: 统一的 pvid 和 logId 管理 hook
 * @FilePath: /isp_pro2/src/hooks/usePvId.ts
 */
import { useCallback, useEffect, useState } from 'react';

import { getPvId } from '@/util';

import { SEARCH_PVID_LOGID } from '@/enum/LocalStorage';

export interface PvIdLogId {
  pvId: string;
  logId: string;
}

export const SEARCH_REFRESH_REPORT_ID_KEY = 'search_refresh_report_id';

export type PvIdUpdateType = 'new_search' | 'filter' | 'page_turn' | 'init';

/**
 * 从 localStorage 安全读取 pvid 和 logId
 */
const getPvIdFromStorage = (): PvIdLogId | null => {
  try {
    const stored = window.localStorage.getItem(SEARCH_PVID_LOGID);
    if (!stored) {
      return null;
    }
    const parsed = JSON.parse(stored);
    if (parsed?.pvId && parsed?.logId) {
      return {
        pvId: parsed.pvId,
        logId: parsed.logId,
      };
    }
    return null;
  } catch (error) {
    console.error('解析 SEARCH_PVID_LOGID 失败:', error);
    return null;
  }
};

/**
 * 保存 pvid 和 logId 到 localStorage
 */
const savePvIdToStorage = (data: PvIdLogId): void => {
  try {
    window.localStorage.setItem(SEARCH_PVID_LOGID, JSON.stringify(data));
  } catch (error) {
    console.error('保存 SEARCH_PVID_LOGID 失败:', error);
  }
};

/**
 * 保存刷新链路统一上报 ID（用于 search_button/result_counts/skuexposure 对齐）
 */
export const setSearchRefreshReportId = (id: string): void => {
  try {
    window.sessionStorage.setItem(SEARCH_REFRESH_REPORT_ID_KEY, id);
  } catch (error) {
    console.error('保存 SEARCH_REFRESH_REPORT_ID_KEY 失败:', error);
  }
};

/**
 * 读取刷新链路统一上报 ID
 */
export const getSearchRefreshReportId = (): string => {
  try {
    return window.sessionStorage.getItem(SEARCH_REFRESH_REPORT_ID_KEY) || '';
  } catch (error) {
    console.error('读取 SEARCH_REFRESH_REPORT_ID_KEY 失败:', error);
    return '';
  }
};

/**
 * 清理刷新链路统一上报 ID
 */
export const clearSearchRefreshReportId = (): void => {
  try {
    window.sessionStorage.removeItem(SEARCH_REFRESH_REPORT_ID_KEY);
  } catch (error) {
    console.error('移除 SEARCH_REFRESH_REPORT_ID_KEY 失败:', error);
  }
};

/**
 * 生成新的 pvid 和 logId
 */
const generateNewPvId = (): PvIdLogId => {
  const newPvId = getPvId();
  return {
    pvId: newPvId,
    logId: newPvId,
  };
};

/**
 * 统一的 pvid 和 logId 管理 Hook
 *
 * @param options 配置选项
 * @param options.autoInit 是否自动初始化（从 localStorage 读取或生成新的）
 * @param options.storageKey 自定义存储 key（默认使用 SEARCH_PVID_LOGID）
 *
 * @returns pvid 管理对象
 *
 * @example
 * ```tsx
 * const { pvId, logId, updatePvId, resetPvId } = usePvId({ autoInit: true });
 *
 * // 新搜索时
 * updatePvId('new_search');
 *
 * // 翻页时
 * updatePvId('page_turn');
 *
 * // 筛选时
 * updatePvId('filter');
 * ```
 */
export const usePvId = (options: { autoInit?: boolean } = {}) => {
  const { autoInit = false } = options;

  // 初始化：优先从 localStorage 读取，如果没有则生成新的
  const [pvIdData, setPvIdData] = useState<PvIdLogId>(() => {
    if (autoInit) {
      const stored = getPvIdFromStorage();
      return stored || generateNewPvId();
    }
    return generateNewPvId();
  });

  // 自动初始化：从 localStorage 读取
  useEffect(() => {
    if (autoInit) {
      const stored = getPvIdFromStorage();
      if (stored) {
        setPvIdData(stored);
        // 同步到 localStorage（确保数据一致）
        savePvIdToStorage(stored);
      } else {
        // 如果没有存储的数据，生成新的并保存
        const newData = generateNewPvId();
        setPvIdData(newData);
        savePvIdToStorage(newData);
      }
    }
  }, [autoInit]);

  /**
   * 更新 pvid 和 logId
   *
   * @param type 更新类型
   * - 'new_search': 新搜索，生成新的 pvId 和 logId
   * - 'filter': 筛选操作，生成新的 pvId 和 logId
   * - 'page_turn': 翻页操作，只更新 logId，保留原 pvId
   * - 'init': 初始化，从 localStorage 读取或生成新的
   * @returns 更新后的 pvid 和 logId 对象（用于同步场景）
   */
  const updatePvId = useCallback(
    (type: PvIdUpdateType = 'init'): PvIdLogId => {
      let newData: PvIdLogId;

      switch (type) {
        case 'new_search':
        case 'filter': {
          // 新搜索或筛选：生成新的 pvId 和 logId
          newData = generateNewPvId();
          break;
        }
        case 'page_turn': {
          // 翻页：只更新 logId，保留当前 pvId
          newData = {
            pvId: pvIdData.pvId || getPvId(),
            logId: getPvId(),
          };
          break;
        }
        case 'init':
        default: {
          // 初始化：优先从 localStorage 读取，如果没有则生成新的
          const stored = getPvIdFromStorage();
          newData = stored || generateNewPvId();
          break;
        }
      }

      // 保存到 localStorage
      savePvIdToStorage(newData);
      // 更新状态
      setPvIdData(newData);
      return newData;
    },
    [pvIdData.logId, pvIdData.pvId],
  );

  /**
   * 重置 pvid 和 logId（生成全新的）
   */
  const resetPvId = useCallback(() => {
    const newData = generateNewPvId();
    setPvIdData(newData);
    savePvIdToStorage(newData);
  }, []);

  /**
   * 手动设置 pvid 和 logId
   */
  const setPvId = useCallback((data: PvIdLogId) => {
    setPvIdData(data);
    savePvIdToStorage(data);
  }, []);

  /**
   * 从 localStorage 同步 pvid 和 logId（不生成新的）
   */
  const syncFromStorage = useCallback(() => {
    const stored = getPvIdFromStorage();
    if (stored) {
      setPvIdData(stored);
    }
  }, []);

  /**
   * 根据搜索场景智能更新 pvid 和 logId
   * 用于搜索列表页面的场景判断（翻页 vs 其他参数变化）
   *
   * @param options 配置选项
   * @param options.searchNumber 当前搜索次数（0 表示第一次搜索）
   * @param options.newPageNo 新的页码（从 URL 参数获取）
   * @param options.currentPage 当前页码（从 pageInfo 获取，如果不存在则默认为 1）
   * @returns 更新后的 pvid 和 logId 对象
   *
   * @example
   * ```tsx
   * const { getPvIdLogIdForSearch } = usePvId({ autoInit: true });
   * const result = getPvIdLogIdForSearch({
   *   searchNumber: 1,
   *   newPageNo: 2,
   *   currentPage: pageInfo?.pageIndex ?? 1,
   * });
   * // result.pvId, result.logId
   * ```
   */
  const getPvIdLogIdForSearch = useCallback(
    (options: { searchNumber: number; newPageNo?: number; currentPage?: number }): PvIdLogId => {
      const { searchNumber, newPageNo, currentPage = 1 } = options;

      if (searchNumber === 0) {
        // 第一次搜索：优先从 localStorage 读取最新值（可能被 QPSearch 中的 updatePvId 更新过）
        // 这样确保与 QPSearch 组件中 updatePvId('new_search') 生成的值保持一致
        const storedPvIdLogId = getPvIdLogId();
        // 同步到 hook 状态，保持一致性
        syncFromStorage();
        return storedPvIdLogId;
      } else {
        // 搜索结果页内仅翻页时保留 pvId，其余参数变化都重置 pvId / logId
        const updated = newPageNo != null && newPageNo !== currentPage ? updatePvId('page_turn') : updatePvId('filter');
        return updated;
      }
    },
    [updatePvId, syncFromStorage],
  );

  return {
    pvId: pvIdData.pvId,
    logId: pvIdData.logId,
    pvIdData, // 完整的 pvid 数据对象
    updatePvId,
    resetPvId,
    setPvId,
    syncFromStorage,
    getPvIdLogIdForSearch,
  };
};

/**
 * 获取 pvid 和 logId（不创建 hook，用于非组件场景）
 * 优先从 localStorage 读取，如果没有则生成新的
 *
 * @returns pvid 和 logId 对象
 */
export const getPvIdLogId = (): PvIdLogId => {
  const stored = getPvIdFromStorage();
  if (stored) {
    return stored;
  }
  const newData = generateNewPvId();
  savePvIdToStorage(newData);
  return newData;
};

/**
 * 更新 pvid 和 logId（不创建 hook，用于非组件场景）
 *
 * @param type 更新类型
 * @returns 更新后的 pvid 和 logId 对象
 */
export const updatePvIdLogId = (type: PvIdUpdateType = 'init'): PvIdLogId => {
  const current = getPvIdFromStorage();
  let newData: PvIdLogId;

  switch (type) {
    case 'new_search':
    case 'filter':
      newData = generateNewPvId();
      break;
    case 'page_turn':
      newData = {
        pvId: current?.pvId || getPvId(),
        logId: getPvId(),
      };
      break;
    case 'init':
    default:
      newData = current || generateNewPvId();
      break;
  }

  savePvIdToStorage(newData);
  return newData;
};
